
import { useEffect, useRef, useState, useCallback, type ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useStore } from '../../stores/StoreProvider';
import { CrmDatabase } from '../../lib/db/database';
import { SyncEngine } from '../../lib/sync/SyncEngine';
import { SyncRefreshContext } from '../../contexts/SyncRefreshContext';

// Maps a server entity type to the React Query keys whose results derive from it.
// MobX-backed entities (contact, deal, task, project, dealType) are NOT listed here —
// the SyncEngine already updates their MobX stores directly; no RQ query reads them.
function invalidateRqForEntities(qc: QueryClient, entityTypes: Set<string>): void {
  const keysToInvalidate = new Set<string>();
  for (const entity of entityTypes) {
    if (entity === 'note' || entity === 'noteAttachment') {
      keysToInvalidate.add('notes');
      keysToInvalidate.add('timeline');
      keysToInvalidate.add('activity-notes');
      keysToInvalidate.add('deal-activity');
      keysToInvalidate.add('project-activity');
    } else if (entity === 'user') {
      // Team list rows carry email/displayName; owner columns read MobX directly.
      keysToInvalidate.add('team');
    } else if (entity === 'auditLog') {
      keysToInvalidate.add('timeline');
      keysToInvalidate.add('deal-activity');
      keysToInvalidate.add('project-activity');
    }
  }
  for (const key of keysToInvalidate) {
    qc.invalidateQueries({ queryKey: [key] });
  }
}

interface SyncProviderProps {
  children: ReactNode;
  orgId: string;
  token: string;
}

export const SyncProvider = observer(function SyncProvider({ children, orgId, token }: SyncProviderProps) {
  const store = useStore();
  const queryClient = useQueryClient();
  const engineRef = useRef<SyncEngine | null>(null);
  const dbRef = useRef<CrmDatabase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const prevOrgIdRef = useRef<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!orgId || !token) return;

    // Skip initialization if the token doesn't match the orgId yet
    // (intermediate state during org switching)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.orgId && payload.orgId !== orgId) return;
    } catch { /* proceed if token can't be decoded */ }

    // Teardown and reinitialize on org switch
    if (prevOrgIdRef.current && prevOrgIdRef.current !== orgId) {
      engineRef.current?.dispose();
      dbRef.current?.close();
      store.clearAll();
      engineRef.current = null;
      dbRef.current = null;
    }
    prevOrgIdRef.current = orgId;

    let disposed = false;

    async function init() {
      try {
        // Always wipe in-memory stores before loading a new session's data.
        // loadFromDatabase() upserts (not replaces), so without this a previous
        // org's data would remain in the MobX stores after switching accounts or orgs.
        store.clearAll();

        const db = new CrmDatabase(orgId);
        await db.open();
        dbRef.current = db;

        const engine = new SyncEngine(db, store, token, (entityTypes) => {
          invalidateRqForEntities(queryClient, entityTypes);
        });
        engineRef.current = engine;

        await engine.initialize();

        if (disposed) return;

        if (!store.syncStore.serverBootstrapDone) {
          if (navigator.onLine) {
            engine.bootstrap().catch((err) => {
              if (!disposed) {
                setError(err instanceof Error ? err.message : 'Bootstrap failed');
              }
            });
          }
          // If offline, SyncEngine.handleOnline() triggers bootstrap when connectivity returns.
        }
      } catch (err) {
        if (disposed) return;
        setError(err instanceof Error ? err.message : 'Sync initialization failed');
        console.error('[SyncProvider] Init error:', err);
      }
    }

    init();

    return () => {
      disposed = true;
      engineRef.current?.dispose();
      dbRef.current?.close();
    };
  }, [orgId, token, store, queryClient, retryKey]);

  useEffect(() => {
    if (engineRef.current && token) {
      engineRef.current.updateToken(token);
    }
  }, [token]);

  // Reflect sync state onto <html> as data-* attributes so E2E (and any future
  // a11y/devtools probes) can observe push-queue depth and connection status
  // without reaching into MobX. Wrapped in observer() above, so this re-fires
  // whenever pendingMutationCount or status changes.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.pendingMutations = String(store.syncStore.pendingMutationCount);
    document.documentElement.dataset.syncStatus = store.syncStore.status;
  }, [store.syncStore.pendingMutationCount, store.syncStore.status]);

  const refresh = useCallback(async () => {
    if (engineRef.current) {
      await engineRef.current.fetchDelta();
    }
  }, []);

  const retry = useCallback(() => {
    setError(null);
    prevOrgIdRef.current = null;
    setRetryKey(k => k + 1);
  }, []);

  return (
    <SyncRefreshContext.Provider value={refresh}>
      {error && (
        <div className="flex items-center gap-3 px-4 py-2 bg-destructive/10 text-destructive text-sm border-b border-destructive/20 shrink-0">
          <p className="flex-1 truncate">Sync error: {error}</p>
          <button
            className="px-3 py-1 bg-primary text-primary-foreground rounded-md text-xs font-medium shrink-0"
            onClick={retry}
          >
            Retry
          </button>
        </div>
      )}
      {children}
    </SyncRefreshContext.Provider>
  );
});
